import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, effect, ElementRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatAccordion, MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatOption, MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TilbyGesturesDirective } from '@tilby/tilby-ui-lib/directives/tilby-gestures';
import { CombinationsFormService } from './combinations-form.service';
import { debounceTime, distinctUntilChanged, filter, map, merge, Observable, of, startWith, Subject, Subscription, switchMap, takeUntil, tap } from 'rxjs';
import { ItemCombinationsFields, ItemsVariationsWithCustomVariationsValue, ItemVariationValuesFields, VariationsFormGroup } from '../item-variations.model';
import { Items, ItemsVariationValues } from 'tilby-models';
import { AlertDialogService, ConfirmDialogService } from 'src/app/dialogs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CustomFormArray, CustomFormGroup } from '@tilby/tilby-ui-lib/components/tilby-magic-form';
import { TruncatedTextDirective } from 'src/app/directives/truncated-text.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BarcodeManagerService, ConfigurationManagerService, EnvironmentInfoService } from 'src/app/core';
import { BarcodeSelectorComponent } from '../../barcode-selector/barcode-selector.component';

@Component({
  selector: 'app-combinations',
  standalone: true,
  imports: [ MatFormFieldModule, ReactiveFormsModule,
    TranslateModule, FormsModule, CommonModule,
    MatInputModule, MatSelectModule,
    MatIconModule, MatButtonModule,
    MatExpansionModule, MatChipsModule,
    MatAutocompleteModule, TilbyGesturesDirective,
    TruncatedTextDirective, MatTooltipModule,
    BarcodeSelectorComponent
  ],
  templateUrl: './combinations.component.html',
  styleUrl: './combinations.component.scss'
})
export class CombinationsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() itemPriceValue: Record<string, number> = {};
  @Input() combinationsData: ItemCombinationsFields[] = []; 
  @Input() variationsData: VariationsFormGroup = new CustomFormArray<CustomFormGroup<ItemsVariationsWithCustomVariationsValue>>([]); 

  @Output() requestAddVariation = new EventEmitter<string>();
  @Output() requestAddVariationValue = new EventEmitter<{index: number, variationValueName: string}>();
  @Output() closeAllVariationsPanel = new EventEmitter<boolean>();

  readonly combinationsFormService = inject(CombinationsFormService);
  private readonly confirmDialogService = inject(ConfirmDialogService);  
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly translate = inject(TranslateService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly environmentInfoService = inject(EnvironmentInfoService);
  private readonly barcodeManagerService = inject(BarcodeManagerService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);

  @ViewChild('combinationAccordion') combinationAccordion: MatAccordion | undefined;
  @ViewChildren(MatExpansionPanel) panels!: QueryList<MatExpansionPanel>;
  @ViewChildren(MatExpansionPanel, { read: ElementRef }) elementRefPanels!:  QueryList<ElementRef>;
  // ViewChildren per gestire lo scroll dei pannelli delle combinazioni
  @ViewChildren('combinationPanelContent', { read: ElementRef }) combinationPanelContents!: QueryList<ElementRef>;

  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChildren(MatAutocompleteTrigger) allAutocompleteTriggers!: QueryList<MatAutocompleteTrigger>;

  @ViewChildren('autocompleteInputElement', { read: ElementRef }) autocompleteInputs!: QueryList<ElementRef>;
  @ViewChildren(MatInput) matInputs!: QueryList<MatInput>;

  filteredVariationValues: Map<number, Array<Observable<ItemVariationValuesFields[]> | undefined>> = new Map();//new Array(of([]));
  // Map per memorizzare l'ultima istantanea delle opzioni filtrate
  latestFilteredOptionsSnapshot: Map<string, ItemVariationValuesFields[]> = new Map();

  private destroy$ = new Subject<void>(); 
  private variationValueNameCombinationSubscriptions = new Map<any, Subscription>();
  private variationValuesCombinationSubscriptions = new Map<any, Subscription>(); 
  
  private resetFilterSubject = new Subject<{combinationIndex: number, variationIndex: number}>();
  protected isCameraBarcodeSupported = this.environmentInfoService.isCameraBarcodeSupported();
  protected useNewBarcodeQty = this.configurationManagerService.getPreference('items.use_new_barcode_qty');

  constructor() {
    effect(() => {
      const updateVariationValueNameListener = this.combinationsFormService.updateVariationValueNameListener();
      if(updateVariationValueNameListener >= 0) {
        this.listenToVariationValueCombinationChanges();
      }
    })
  }

  ngOnInit(): void {
    this.combinationsFormService.createForm(this.combinationsData, this.variationsData);

    // this.combinationsFormService.combinationsArray?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
    //   console.log("combinationsArray changed: ", this.combinationsFormService.combinationsArray.value);
    // })

    this.listenToCombinationsChanges();
    this.listenToVariationValueCombinationChanges();

    this.updateVariationValueNameCombinationsSubscriptions();
  }

  ngAfterViewInit(): void { 
    setTimeout(() => {
      if(this.combinationAccordion) {
        this.combinationAccordion.closeAll();
      }
      this.initVariationValuesCombination();
    })
  }

  initVariationValuesCombination() {
    this.combinationsFormService.combinationsArray?.value.forEach((combination, combinationIndex) => {
      this.combinationsFormService.getCombinationValues(combinationIndex)?.value.forEach((combinationValue, combinationValueIndex) => {
        const targetValue = this.combinationsFormService.getVariationValueCombination(combinationIndex, combinationValueIndex)?.value;
        if(targetValue) {
          this.selectOptionValueFromList(targetValue, combinationIndex, combinationValueIndex);
        }
      })
    })
  }

  listenToCombinationsChanges() {
    this.combinationsFormService.combinationsArray?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(50),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe(() => {
        this.updateVariationValueNameCombinationsSubscriptions();
      });
  }

  updateVariationValueNameCombinationsSubscriptions() {
    const currentControls = new Set(this.combinationsFormService.combinationsArray?.value
        .flatMap(combination => {
          const innerList = combination.combination_values || [];
          return innerList.map(v => v.tmp_variation_value_id);
        }) || []
    );

    // Rimozione delle sottoscrizioni per i controlli che non esistono più
    this.variationValueNameCombinationSubscriptions.forEach((sub, control) => {
      if (!currentControls.has(control)) {
        sub.unsubscribe();
        this.variationValueNameCombinationSubscriptions.delete(control);
      }
    });

    // Aggiunta sottoscrizioni per i nuovi controlli o quelli esistenti non ancora sottoscritti
    this.combinationsFormService.combinationsArray?.controls.forEach((formGroupCombination: any, combinationIndex: number) => {
      this.combinationsFormService.getCombinationValues(combinationIndex)?.controls.forEach((formGroupCombinationValue: any, combinationValueIndex: number) => {
        if (!this.variationValueNameCombinationSubscriptions.has(this.combinationsFormService.getTmpVariationValueIdCombination(combinationIndex, combinationValueIndex)?.value)) {
          const nameControl = this.combinationsFormService.getVariationValueCombination(combinationIndex, combinationValueIndex);
          if (nameControl) {
            const sub = nameControl.valueChanges
              .pipe(takeUntil(this.destroy$))
              .subscribe((value: string) => {
                this.combinationsFormService.updateOneNameComposition(combinationIndex);
              });
            this.variationValueNameCombinationSubscriptions.set(this.combinationsFormService.getTmpVariationValueIdCombination(combinationIndex, combinationValueIndex)?.value, sub);
          }
        }
      })
    });
  }

  listenToVariationValueCombinationChanges(){
    this.combinationsFormService.updateVariationValueInCombination(this.variationsData);

    this.variationValuesCombinationSubscriptions.forEach(sub => sub.unsubscribe());
    this.variationValuesCombinationSubscriptions.clear();

    // Pulisco filteredVariationValues e latestFilteredOptionsSnapshot per evitare di mantenere riferimenti obsoleti
    this.filteredVariationValues.clear();
    this.latestFilteredOptionsSnapshot.clear();

    this.combinationsFormService.combinationsArray?.value.forEach((element, combinationIndex) => {

      // Inizializzo l'array esterno di Observables per questa combinationIndex
      const outerArrayOfInnerArrays: Observable<any>[] = [];
      this.filteredVariationValues.set(combinationIndex, outerArrayOfInnerArrays);

      this.combinationsFormService.getCombinationValues(combinationIndex)?.value.forEach((combination, variationIndex) => {
        const controlKey = `${combinationIndex}_${variationIndex}`;
        
        // Lista di valori filtrabili per la combinazione e il valore variante specifico 
        const filterableValues = this.combinationsFormService.getVariationValueListCombination(combinationIndex, variationIndex)?.value || [];

        this.latestFilteredOptionsSnapshot.set(controlKey, [...filterableValues]);

        // Create il nuovo stream Observable
        const currentControl = this.combinationsFormService.getVariationValueCombination(combinationIndex, variationIndex);

        if (currentControl) {
          // Crea un Observable che emette il valore del controllo O null quando c'è un reset specifico
          const valueOrReset$ = merge(
            currentControl.valueChanges.pipe(
              startWith(currentControl.value),
              // filter(value => typeof value === 'string' || value === null)
            ),
            // Quando si riceve un reset, mappa a null per resettare il filtro
            this.resetFilterSubject.pipe(
              filter(reset => reset.combinationIndex === combinationIndex && reset.variationIndex === variationIndex),
              map(() => null) 
            ),
            // this.initFilterSubject.pipe(
            //   filter(init => init.combinationIndex === combinationIndex && init.variationIndex === variationIndex),
            //   map((init) => {
            //     this.selectOptionValueFromList(init.targetValue, init.combinationIndex, init.variationIndex);
            //     return init.targetValue
            //   })
            // ),
          );

          const newFilteredObservableStream = valueOrReset$.pipe(
            switchMap(value => {
              const filterValue = (typeof value === 'object' && value !== null && 'value' in value) 
                                ? value.value 
                                : value;

              const valueList = filterValue ? this._filter(filterValue, filterableValues) : filterableValues.slice();
              return of(valueList);
            }),
            tap(list => {
              this.latestFilteredOptionsSnapshot.set(controlKey, list);
            }),
            takeUntil(this.destroy$)
          );
    
          outerArrayOfInnerArrays[variationIndex] = newFilteredObservableStream;
        }
      });
    });
  }
  
  private _filter(value: string | ItemsVariationValues, options: ItemsVariationValues[]): ItemsVariationValues[] {
    let textValue = '';
    if(typeof value === 'string') {
      textValue = value;
    } else if(value.value) {
      textValue = value.value;
    }

    const filterValue = textValue?.toLowerCase();
    return options.filter(option => option.value?.toLowerCase().includes(filterValue));
  }

  displayOption(option: ItemVariationValuesFields): string {
    // Gestisce il caso in cui option sia null, undefined o un oggetto con 'value'
    if (option && typeof option === 'object' && option.value) {
        return option.value;
    }
    // Gestisce il caso in cui option sia una stringa (testo digitato nell'input)
    if (typeof option === 'string') {
        return option;
    }
    return '';
  }

  addCombination() {
    this.panels.forEach(panel => panel.expanded = false);

    this.combinationsFormService.addNewCombination(
      this.variationsData,
      {
        price1: this.itemPriceValue?.['price1' as keyof Items],
        price2: this.itemPriceValue?.['price2' as keyof Items],
        price3: this.itemPriceValue?.['price3' as keyof Items],
        price4: this.itemPriceValue?.['price4' as keyof Items],
        price5: this.itemPriceValue?.['price5' as keyof Items]
      }
    );
    const panelId = this.combinationsFormService.combinationsArray?.length;

    this.scrollToCombinationPanel(panelId? panelId - 1: 0);
    this.combinationsFormService.updateNameComposition();

    this.listenToVariationValueCombinationChanges();
  }

  async generateCombinations() {
    let message = "";

    const valuesPerCombination = this.variationsData.value.map(variation => variation.variation_values?.length);
    const numberOfCombinations = valuesPerCombination?.reduce((a, b) => (a || 0) * (b || 0), 1) || 0;

    if (numberOfCombinations > 64) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.DETAILS.TOO_MUCH_COMBINATIONS')
        }})
      return;
    }

    if (numberOfCombinations === 0) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.DETAILS.VARIATION_HAS_NO_VALUES')
        }})
        return;
    }

    if(this.combinationsFormService.combinationsArray?.length) {
        message += this.translate.instant('ITEMS.DETAILS.AUTO_GENERATION_DELETES_ALL_COMBINATIONS');
    }

    if (message) {
      const confirm = await this.confirmDialogService.openDialog({
          data: {
              messageLabel: message + this.translate.instant('ITEMS.DETAILS.WANT_TO_PROCEED'),
              confirmLabel: 'DIALOG.CONFIRM.YES',
              cancelLabel: 'DIALOG.CONFIRM.NO'
          }
      });

      if(!confirm) {
        return;
      }
    }

    const prices = {
      price1: this.itemPriceValue?.['price1' as keyof Items],
      price2: this.itemPriceValue?.['price2' as keyof Items],
      price3: this.itemPriceValue?.['price3' as keyof Items],
      price4: this.itemPriceValue?.['price4' as keyof Items],
      price5: this.itemPriceValue?.['price5' as keyof Items]
    }

    this.combinationsFormService.generateAllCombinations(this.variationsData, prices);
    this.listenToVariationValueCombinationChanges();

    setTimeout(() => {
      this.closeAllCombinationPanels();
    })
  }

  addVariationFromCombination(combinationIndex: number,) {
    if(this.combinationsFormService.getNameAddedVariantCombination(combinationIndex)?.value) {
      this.requestAddVariation.emit(this.combinationsFormService.getNameAddedVariantCombination(combinationIndex)?.value);
      this.combinationsFormService.getNameAddedVariantCombination(combinationIndex)?.setValue(null);
    }
  }
  
  addVariationValueFromCombination(combinationIndex: number, combinationValueIndex: number) {
    if(this.combinationsFormService.getVariationValueCombination(combinationIndex, combinationValueIndex)?.value) {
      this.requestAddVariationValue.emit({index: combinationValueIndex, variationValueName: this.combinationsFormService.getVariationValueCombination(combinationIndex, combinationValueIndex)?.value});
      this.listenToVariationValueCombinationChanges();
      const variationValues = this.variationsData.at(combinationIndex)?.get('variation_values')?.value || [];
      this.combinationsFormService.updateVariationValueValidators(combinationValueIndex, variationValues);
    }
  }

  longPress($event: any, combinationIndex: number, combinationValueIndex: number) {
    this.addVariationValueFromCombination(combinationIndex, combinationValueIndex);
  }

  stopFocusAddVariationBtn: boolean = true;

  onKeydownVariation(event: KeyboardEvent, i: number, clearVariationBtn: MatButton, addVariationBtn: MatButton) {
    if(event.key === 'Enter' && !addVariationBtn?.disabled) {
      this.addVariationFromCombination(i);
    } 
    else if(event.key === 'Tab' && !event.shiftKey) {
      if(clearVariationBtn) {
        this.stopFocusClearNameVariationBtn = false;
      } else if(!addVariationBtn?.disabled) {
        this.stopFocusAddVariationBtn = false;
      }
    }
  }

  onBlurAddVariationButton(event: FocusEvent, i: number): void {
    const addButton = event.target as HTMLButtonElement;
    if (document.activeElement !== addButton && !this.stopFocusAddVariationBtn) {
      addButton.focus();
    }
  }

  onKeydownAddVariationButton(event: KeyboardEvent, i: number): void {
    if(event.key === 'Tab') {
      this.stopFocusAddVariationBtn = true;
      if(event.shiftKey) {
        this.stopFocusClearNameVariationBtn = false;
      }
    }
  }

  stopFocusClearNameVariationBtn: boolean = true;

  onBlurClearNameVariationButton(event: FocusEvent, i: number): void {
    const clearButton = event.target as HTMLButtonElement;
    if (document.activeElement !== clearButton && !this.stopFocusClearNameVariationBtn) {
      clearButton.focus();
    }
  }

  onKeydownClearNameVariationButton(event: KeyboardEvent, i: number, addButton: MatButton): void {
    if(event.key === 'Tab') {
      this.stopFocusClearNameVariationBtn = true;
      if(addButton && !addButton.disabled && !event.shiftKey) {
        this.stopFocusAddVariationBtn = false;
      }
    }
  }

  onClickClearNameVariationBtn(event: MouseEvent, i: number) {
    this.combinationsFormService.getNameAddedVariantCombination(i)?.setValue('');
  }

  onVariationInput(event: Event, i: number, j: number): void {
    const inputValue = (event.target as HTMLInputElement).value;
    if(inputValue == '') {
      this.combinationsFormService.getVariationValueCombination(i, j)?.markAsPristine();
      this.combinationsFormService.getVariationValueCombination(i, j)?.markAsUntouched();
    }
  }

  disableAddNewVariationVarlueFromCombination(combinationIndex: number, combinationValueIndex: number) {
    if(!this.combinationsFormService.getVariationValueCombination(combinationIndex, combinationValueIndex)?.value) return true;
    if(this.combinationsFormService.getVariationValueCombination(combinationIndex, combinationValueIndex)?.value?.tmp_id) return true;

    const valueList = this.getLatestFilteredOptions(combinationIndex, combinationValueIndex);
    if(valueList?.length) {
      const valExist = valueList.findIndex(v => v.value?.toLowerCase() == this.combinationsFormService.getVariationValueCombination(combinationIndex, combinationValueIndex)?.value?.toLowerCase());

      if(valExist != undefined && valExist >= 0) {
        return true;
      }
    }
    return false;
  }

  onKeydownVariationValue(event: KeyboardEvent, i: number, j: number, clearButton: MatButton, addButton: MatButton): void {
    this.lastKeydownIsEnter = (event.key === 'Enter');
    if(event.key === 'Enter') {
      if(!this.disableAddNewVariationVarlueFromCombination(i, j)) {
        this.addVariationValueFromCombination(i, j);
      }
      setTimeout(() => {
          const target = this.combinationsFormService.getVariationValueCombination(i, j)?.value?.toLowerCase() || '';
          this.selectOptionValueFromList(target, i, j);
  
      })
    } 
    else if(event.key === 'Tab') {
      if(!event.shiftKey || addButton?.disabled) {
        if(clearButton) {
          this.stopFocusClearBtn = false;
        }
      } else if(event.shiftKey && !addButton.disabled) {
        if(addButton) {
          this.stopFocusAddBtn = false;
        }
      }
    }

    setTimeout(() => {
      this.lastKeydownIsEnter = false;
    })
  }

  
  selectOptionValueFromList(target: any, i: number, j: number) {
    if(target) {
      const targetValue = typeof target === 'string'? target.toLowerCase(): target.value?.toLowerCase();
      const specificTrigger = this.allAutocompleteTriggers.get(j);
      const options: QueryList<MatOption> | undefined = specificTrigger?.autocomplete?.options;

      const foundOption: MatOption<any> | undefined = options?.toArray().find(
        (option: MatOption) => {
          const optionValue = option.value as any;
          return optionValue && optionValue.value?.toLowerCase() === targetValue;
        }
      );

      if(foundOption) {
        const hasDuplicateCombination = this.checkDuplicatedComposition(i, j);
        if(!hasDuplicateCombination) {
          foundOption.select();
          this.combinationsFormService.getVariationValueCombination(i, j)?.setValue(foundOption.value);
        } else {
          this.combinationsFormService.getVariationValueCombination(i, j)?.setValue(null, {emitEvent: false});
          specificTrigger?.closePanel();
        }
      }
    }
  }
  
  checkDuplicatedComposition(combinationIndex: number, combinationValueIndex: number): boolean {
    const currentCombinationValuesControl = this.combinationsFormService.getCombinationValues(combinationIndex)?.value;
    let hasDuplicateCombination = false;

    const combinations = this.combinationsFormService.combinationsArray?.value;

    hasDuplicateCombination = combinations?.some((c) => {
      const combinationValue = c.combination_values || [];
      if (combinationValue === currentCombinationValuesControl) {
        return false;
      }

      //Check if this combination has the same combination_values
      for (let i = 0; i < combinationValue.length; i++) {
        if ((typeof currentCombinationValuesControl?.[i]?.variation_value === 'string' && 
          currentCombinationValuesControl?.[i]?.variation_value?.toLowerCase() != combinationValue?.[i]?.variation_value?.value?.toLowerCase()) ||
          (typeof currentCombinationValuesControl?.[i]?.variation_value === 'object' &&
        (currentCombinationValuesControl?.[i]?.variation_value?.value?.toLowerCase() || null) != (combinationValue?.[i]?.variation_value?.value?.toLowerCase() || null))) {
          return false;
        }
      }

      return true;
    }) || false;

    if (hasDuplicateCombination) {
      this.alertDialogService.openDialog({
      data: {
        messageLabel: this.translate.instant('ITEMS.DETAILS.COMBINATION_HAS_BEEN_DUPLICATED')
      }})
    }
    return hasDuplicateCombination;
  }

  onBlurVariationValueCombinationSection(event: FocusEvent, i: number, j:number, container: any, trigger: MatAutocompleteTrigger) {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const targetElement = event.target as HTMLElement;
    if (trigger.panelOpen) {
      return;
    }

    if(relatedTarget?.id === 'clearVariationValueBtnId' || 
      event.view?.document.activeElement?.id === 'clearVariationValueBtnId' ||
      targetElement?.id === 'clearVariationValueBtnId' 
      // || relatedTarget?.id === 'addVariationValueBtnId' || 
      // targetElement?.id === 'addVariationValueBtnId' 
      // || targetElement?.id === 'inputVariationValueId'
    ) {
      return;
    }
    
    
    if ((relatedTarget && !container.contains(relatedTarget)) || (!relatedTarget && targetElement != event.view?.document.activeElement)) {
      if(!this.combinationsFormService.getVariationValueCombination(i, j)?.value?.tmp_id) 
      {
        this.combinationsFormService.getVariationValueCombination(i, j)?.setValue(null);
        this.resetFilterSubject.next({combinationIndex: i, variationIndex: j});
      }
    }
    return;
  }

  stopFocusClearBtn: boolean = true;

  onBlurClearButton(event: FocusEvent, i: number, j: number): void {
    const clearButton = event.target as HTMLButtonElement;
    if (document.activeElement !== clearButton && !this.stopFocusClearBtn) {
      clearButton.focus();
    }
  }

  onKeydownClearButton(event: KeyboardEvent, i: number, j: number, addButton: MatButton): void {
    if(event.key === 'Tab') {
      this.stopFocusClearBtn = true;
      if(addButton && !addButton.disabled && !event.shiftKey) {
        this.stopFocusAddBtn = false;
      }
    }
  }

  onClickClearBtn(event: MouseEvent, i: number, j: number) {
    const target = this.combinationsFormService.getVariationValueCombination(i, j)?.value;
    if(target?.id) {
      const linearIndex = (((i * (this.combinationsFormService.getCombinationValues(0)?.length || 0)) + i) + j);
      const specificTrigger = this.allAutocompleteTriggers.get(linearIndex);
      const options: QueryList<MatOption> | undefined = specificTrigger?.autocomplete?.options;
  
      const foundOption: MatOption<any> | undefined = options?.toArray().find(
        (option: MatOption) => {
          const optionValue = option.value as any;
          return optionValue && optionValue.value === target.value;
        }
      );

      if(foundOption) {
        foundOption.deselect();
      }
    }
    this.combinationsFormService.getVariationValueCombination(i, j)?.setValue(null);

  }

  stopFocusAddBtn: boolean = true;

  onBlurAddVariationValueButton(event: FocusEvent, i: number, j: number): void {
    const addButton = event.target as HTMLButtonElement;
    if (document.activeElement !== addButton && !this.stopFocusAddBtn) {
      addButton.focus();
    }
  }

  onKeydownAddVariationValueButton(event: KeyboardEvent, i: number, j: number): void {
    if(event.key === 'Tab') {
      this.stopFocusAddBtn = true;
      if(event.shiftKey) {
        this.stopFocusClearBtn = false;
      }
    }
  }

  getLatestFilteredOptions(i: number, j: number): ItemVariationValuesFields[] {
    return this.latestFilteredOptionsSnapshot.get(`${i}_${j}`) || [];
  }

  private lastKeydownIsEnter: boolean = false;

  onOptionSelected(combinationIndex: number, combinationValueIndex: number, event: MatAutocompleteSelectedEvent): void {
    const selectedValue = event.option.value;

    if(!this.lastKeydownIsEnter) {
      this.selectOptionValueFromList(selectedValue, combinationIndex, combinationValueIndex)
    }
  }

  removeCombination(combinationIndex: number) {
    this.combinationsFormService.removeCombination(combinationIndex);
    this.listenToVariationValueCombinationChanges();
  }

  closeAllCombinationPanels(): void {
    if (this.combinationAccordion) {
      this.combinationAccordion.closeAll();
    }
  }

  openCombinationPanel(combinationIndex: number) {
    this.closeAllVariationsPanel.emit(true);
    this.combinationsFormService.getExpandedCombination(combinationIndex)?.setValue(true);
  }

  closeCombinationPanel(combinationIndex: number) {
    this.combinationsFormService.getExpandedCombination(combinationIndex)?.setValue(false);
  }

  onAfterCombinationPanelExpand(combinationIndex: number): void {
    this.scrollToCombinationPanel(combinationIndex);
  }

  scrollToCombinationPanel(combinationIndex: number) {
    // Mi assicuro che il DOM sia aggiornato
    requestAnimationFrame(() => {
      const targetElement = this.elementRefPanels?.find(
        (el: ElementRef) => el.nativeElement.querySelector(`#combination-panel-content-${combinationIndex}`)
      )?.nativeElement;
      
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        // Cerco di impostare il focus sul primo input del nome della variazione aggiunta
        // elementToFocus = targetElement.querySelector('input[formControlName="variation_value"]') as HTMLInputElement;

        // if (elementToFocus) {
        //   elementToFocus.focus();
        // }
      }
    });
  }

  getSafeHtml(htmlString: string | null | undefined): SafeHtml {
    if (!htmlString) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustHtml(htmlString);
  }

  getPlainText(htmlString: string | null | undefined): string {
    if (!htmlString) {
      return '';
    }
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  
  removeBarcode(barcode: string, combinationIndex: number) {
    const index = this.combinationsFormService.getBarcodesCombination(combinationIndex)?.value.findIndex((ele: any) => ele.barcode === barcode);
    if (index !== undefined && index >= 0) {
      const currentValue = this.combinationsFormService.getBarcodesCombination(combinationIndex)?.value || [];
      const updatedList = currentValue.filter((_: any, i: number) => i !== index);
      this.combinationsFormService.getBarcodesCombination(combinationIndex)?.setValue(updatedList);
    }
  }

  addBarcodeFromChip(event: MatChipInputEvent, combinationIndex: number): void {
    const value = (event.value || '').trim();
    this.addBarcode(value, combinationIndex);

    // Clear the input value
    event.chipInput!.clear();
  }

  protected async openBarcodeAndSearch(combinationIndex: number) {
    const barcode = await this.barcodeManagerService.openCameraBarcode();
    this.addBarcode(barcode, combinationIndex);
  }

  addBarcode(value: string, combinationIndex: number) {
    if (value) {
      const index = this.combinationsFormService.getBarcodesCombination(combinationIndex)?.value.findIndex((ele: any) => ele.barcode === value);
      if (index !== undefined && index >= 0) {
        this.alertDialogService.openDialog({
          data: {
            messageLabel: this.translate.instant('ITEMS.DETAILS.BARCODE_ALREADY_PRESENT')
        }});
      } else {
        const currentValue = this.combinationsFormService.getBarcodesCombination(combinationIndex)?.value || [];
        this.combinationsFormService.getBarcodesCombination(combinationIndex)?.setValue([...currentValue, {barcode: value}]);
      }
    }
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this.variationValueNameCombinationSubscriptions.forEach(sub => sub.unsubscribe());
    this.variationValueNameCombinationSubscriptions.clear();

    this.variationValuesCombinationSubscriptions.forEach(sub => sub.unsubscribe());
    this.variationValuesCombinationSubscriptions.clear();
  }
}
